import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { IPublicClientApplication, AccountInfo } from '@azure/msal-browser';

/**
 * Microsoft Graph API Service for Group Overage Handling
 * 
 * When a user belongs to > 200 Azure AD groups, the groups claim is removed
 * from the JWT token and replaced with overage indicators (_claim_names, _claim_sources).
 * 
 * This service handles fetching the user's groups via Microsoft Graph API
 * when overage is detected.
 */
export class GraphGroupService {
  /**
   * Retrieve user's group membership from Microsoft Graph API
   * Called when group overage occurs (user has > 200 groups)
   * 
   * @param msalInstance - MSAL instance for token acquisition
   * @param account - User account from MSAL
   * @returns Array of group Object IDs
   */
  static async getUserGroups(
    msalInstance: IPublicClientApplication,
    account: AccountInfo
  ): Promise<string[]> {
    try {
      console.log('🔄 [Graph API] Acquiring token for group membership query...');
      
      // Acquire token with GroupMember.Read.All scope
      // This requires admin consent in Azure Portal
      const response = await msalInstance.acquireTokenSilent({
        scopes: ['GroupMember.Read.All'],
        account: account,
      });

      console.log('✅ [Graph API] Token acquired, calling Graph API...');

      // Call Microsoft Graph to get user's transitive group membership
      // transitiveMemberOf includes nested groups (user -> group A -> group B)
      const graphResponse = await fetch(
        'https://graph.microsoft.com/v1.0/me/transitiveMemberOf/microsoft.graph.group?$select=id',
        {
          headers: {
            Authorization: `Bearer ${response.accessToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!graphResponse.ok) {
        const errorText = await graphResponse.text();
        throw new Error(`Graph API error ${graphResponse.status}: ${errorText}`);
      }

      const data = await graphResponse.json();
      
      // Extract group IDs from response
      const groupIds = data.value.map((group: any) => group.id);

      console.log(`✅ [Graph API] Retrieved ${groupIds.length} groups via transitive membership`);

      return groupIds;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        console.warn('⚠️ [Graph API] Interactive consent required for GroupMember.Read.All permission');
        
        // Attempt to acquire consent via popup
        try {
          console.log('🔄 [Graph API] Requesting user consent via popup...');
          await msalInstance.acquireTokenPopup({
            scopes: ['GroupMember.Read.All'],
            account: account,
          });
          
          // Retry after consent
          console.log('✅ [Graph API] Consent granted, retrying group fetch...');
          return GraphGroupService.getUserGroups(msalInstance, account);
        } catch (consentError) {
          console.error('❌ [Graph API] User declined consent or consent failed:', consentError);
          throw new Error('User consent required for group membership verification');
        }
      }
      
      console.error('❌ [Graph API] Failed to retrieve groups:', error);
      throw error;
    }
  }

  /**
   * Check if the ID token has group overage indicators
   * 
   * @param idTokenClaims - ID token claims from MSAL account
   * @returns true if overage detected
   */
  static hasGroupOverage(idTokenClaims: any): boolean {
    // Check for overage indicators
    // _claim_names.groups or hasgroups claim indicates overage
    return !!(
      idTokenClaims?._claim_names?.groups || 
      idTokenClaims?.hasgroups
    );
  }

  /**
   * Get groups from ID token or Graph API (with automatic fallback)
   * 
   * @param msalInstance - MSAL instance
   * @param account - User account
   * @param idTokenClaims - ID token claims
   * @returns Array of group IDs
   */
  static async getGroupsWithFallback(
    msalInstance: IPublicClientApplication,
    account: AccountInfo,
    idTokenClaims: any,
    groupsClaimName: string = 'groups'
  ): Promise<{
    groups: string[];
    source: 'id_token' | 'graph_api';
    hadOverage: boolean;
  }> {
    const hasOverage = GraphGroupService.hasGroupOverage(idTokenClaims);
    const groupsFromToken = idTokenClaims?.[groupsClaimName] || [];

    // If we have groups in token and no overage, use them
    if (Array.isArray(groupsFromToken) && groupsFromToken.length > 0 && !hasOverage) {
      console.log(`✅ [Groups] Found ${groupsFromToken.length} groups in ID token`);
      return {
        groups: groupsFromToken,
        source: 'id_token',
        hadOverage: false
      };
    }

    // If overage detected or no groups in token, fetch from Graph API
    if (hasOverage || groupsFromToken.length === 0) {
      console.warn(`⚠️ [Groups] Group overage detected or no groups in token`);
      console.warn(`⚠️ [Groups] User likely has > 200 groups. Fetching from Graph API...`);
      
      try {
        const graphGroups = await GraphGroupService.getUserGroups(msalInstance, account);
        return {
          groups: graphGroups,
          source: 'graph_api',
          hadOverage: hasOverage
        };
      } catch (error) {
        console.error('❌ [Groups] Graph API fallback failed:', error);
        // Return empty array if Graph API fails
        // The calling code should fall back to email whitelist
        return {
          groups: [],
          source: 'graph_api',
          hadOverage: hasOverage
        };
      }
    }

    // Fallback: return token groups even if overage detected (shouldn't happen)
    return {
      groups: Array.isArray(groupsFromToken) ? groupsFromToken : [],
      source: 'id_token',
      hadOverage: hasOverage
    };
  }
}
